import { motion } from "framer-motion"
import {Award, BookOpen, Heart, Shield} from 'lucide-react'
import { Link } from "react-router-dom"
import HavenNav from "@/components/HavenNav"

const teamMembers = [
  {
    name: "Dr. Saba Baig",
    role: "Founder & Clinical Director",
    credentials: "PharmD, BSc, CDE",
    image: "https://static.lumi.new/76/76dfba6e83c663cc75ec4145e4879a20.webp",
    bio: "Dr. Saba Baig is a board-certified pharmacist in diabetes education with a passion for integrative health and women's health. She is a member of the North American Menopause Society, offering evidence-based recommendations in hormone replacement therapy (HRT). She holds prescriptive authority for minor ailment prescribing and has helped many clients achieve positive skin results through prescription topical treatments. In addition, she has developed evidence-based protocols alongside our Medical Director, Nicole Ong, to offer premium IV nutrient services.",
    specialties: ["Certified Diabetes Educator", "Women's Health", "Menopause & HRT", "IV Nutrient Therapy", "Integrative Health", "Functional Medicine"],
    quote: "True healthcare begins with listening. Understanding not just symptoms, but the whole person."
  }
]

const values = [
  {
    icon: Heart,
    title: "Patient-Centered",
    description: "Every treatment plan begins with deep listening and understanding of your unique health story."
  },
  {
    icon: Award,
    title: "Clinical Excellence",
    description: "Our team holds advanced certifications and stays current with the latest evidence-based practices."
  },
  {
    icon: BookOpen,
    title: "Education First",
    description: "We empower you with knowledge, so you can make informed decisions about your health journey."
  },
  {
    icon: Shield,
    title: "Trust & Safety",
    description: "Built on transparency, ethical practice, and an unwavering commitment to your well-being."
  }
]

function Team() {
  return (
    <div className="min-h-screen bg-[#FAF8F5]">
      {/* Shared Haven Navigation */}
      <HavenNav />

      {/* Hero */}
      <section className="pt-32 pb-16 lg:pt-40 lg:pb-24">
        <div className="max-w-5xl mx-auto px-5 sm:px-6 lg:px-12 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <p className="text-sm uppercase tracking-[0.25em] text-[#9B8E7E] mb-6 font-medium">
              The People Behind Your Care
            </p>
            <h1 className="text-3xl sm:text-5xl lg:text-7xl font-light mb-6 sm:mb-8 text-[#3D3A34] leading-tight" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
              Meet the Team
            </h1>
            <p className="text-xl text-[#6B6761] font-light max-w-2xl mx-auto leading-relaxed">
              A collective of dedicated healthcare professionals united by a shared belief: 
              that exceptional care is personal, compassionate, and grounded in clinical expertise.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Team Members */}
      <section className="pb-20 lg:pb-32">
        <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-12">
          <div className="space-y-24 lg:space-y-32">
            {teamMembers.map((member, index) => (
              <motion.div
                key={member.name}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8 }}
                viewport={{ once: true, margin: "-100px" }}
                className={`grid lg:grid-cols-2 gap-12 lg:gap-20 items-center ${
                  index % 2 === 1 ? "lg:direction-rtl" : ""
                }`}
              >
                {/* Image - alternates sides */}
                <div className={`${index % 2 === 1 ? "lg:order-2" : ""}`}>
                  <div className="relative">
                    <div className="rounded-3xl overflow-hidden bg-[#FAF8F5]">
                      <img
                        src={member.image}
                        alt={member.name}
                        className="w-full h-[350px] sm:h-[420px] lg:h-[520px] object-contain object-center block bg-[#F5F2EE]"
                      />
                    </div>
                    {/* Floating quote card */}
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.6, delay: 0.3 }}
                      viewport={{ once: true }}
                      className={`absolute -bottom-6 sm:-bottom-8 ${
                        index % 2 === 0 ? "right-2 sm:-right-4 lg:-right-8" : "left-2 sm:-left-4 lg:-left-8"
                      } bg-white p-4 sm:p-6 rounded-xl sm:rounded-2xl shadow-lg max-w-[220px] sm:max-w-[280px]`}
                    >
                      <p className="text-sm italic text-[#6B6761] leading-relaxed" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                        "{member.quote}"
                      </p>
                    </motion.div>
                  </div>
                </div>

                {/* Content */}
                <div className={`${index % 2 === 1 ? "lg:order-1" : ""} pt-8 lg:pt-0`}>
                  <div className="mb-2">
                    <span className="text-xs uppercase tracking-[0.2em] text-[#9B8E7E] font-medium">
                      {member.credentials}
                    </span>
                  </div>
                  <h2 className="text-3xl sm:text-4xl lg:text-5xl font-light mb-3 text-[#3D3A34]" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                    {member.name}
                  </h2>
                  <p className="text-lg text-[#9B8E7E] mb-8 font-light">
                    {member.role}
                  </p>
                  <p className="text-[#6B6761] leading-relaxed text-lg mb-8">
                    {member.bio}
                  </p>
                  <div className="flex flex-wrap gap-3">
                    {member.specialties.map((specialty) => (
                      <span
                        key={specialty}
                        className="px-4 py-2 bg-[#F0EDE8] text-[#6B6761] rounded-full text-sm"
                      >
                        {specialty}
                      </span>
                    ))}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Our Values */}
      <section className="py-14 sm:py-20 lg:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-12">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h3 className="text-4xl lg:text-5xl font-light mb-4 text-[#3D3A34]" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
              What guides us
            </h3>
            <p className="text-lg text-[#6B6761] max-w-2xl mx-auto">
              Our shared values shape every interaction, every treatment plan, and every patient relationship.
            </p>
          </motion.div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => (
              <motion.div
                key={value.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="text-center p-8"
              >
                <div className="w-16 h-16 bg-[#F0EDE8] rounded-2xl flex items-center justify-center mx-auto mb-6">
                  <value.icon className="w-7 h-7 text-[#9B8E7E]" />
                </div>
                <h4 className="text-xl font-serif mb-3 text-[#3D3A34]" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                  {value.title}
                </h4>
                <p className="text-sm text-[#6B6761] leading-relaxed">
                  {value.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Join Our Team CTA */}
      <section className="py-14 sm:py-20 lg:py-28 bg-[#F0EDE8]">
        <div className="max-w-4xl mx-auto px-5 sm:px-6 lg:px-12 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h3 className="text-4xl lg:text-5xl font-light mb-6 text-[#3D3A34]" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
              Ready to experience the Haven difference?
            </h3>
            <p className="text-lg text-[#6B6761] mb-10 max-w-2xl mx-auto leading-relaxed">
              Our team is here to guide you through every step of your wellness journey. 
              Book a consultation and discover personalized care that truly puts you first. Our registered nurses also provide <Link to="/havenwellnesshealth#event-inquiry" className="underline hover:text-[#3D3A34] transition-colors">wedding party IV hydration</Link> for brides and private events.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href="https://havenintegrativehealth.janeapp.com/login" target="_blank" rel="noopener noreferrer" className="px-10 py-5 bg-[#9B8E7E] text-white rounded-full font-medium text-lg hover:bg-[#8A7D6D] hover:shadow-xl transition-all inline-flex items-center justify-center">
                Book Your Consultation
              </a>
              <Link
                to="/"
                className="px-10 py-5 bg-white text-[#3D3A34] rounded-full font-medium text-lg border border-[#E8E4DE] hover:border-[#9B8E7E] transition-all inline-flex items-center justify-center"
              >
                Explore Services
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#3D3A34] text-white py-12">
        <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-12">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 sm:gap-8 mb-8">
            <div>
              <h4 className="text-2xl font-serif mb-4" style={{ fontFamily: "'Cormorant Garamond', serif" }}>Haven</h4>
              <p className="text-white/70 text-sm">Clinical expertise. Elevated wellness.</p>
            </div>
            <div>
              <h5 className="font-medium mb-4">Services</h5>
              <ul className="space-y-2 text-sm text-white/70">
                <li><a href="#" className="hover:text-white transition-colors">Travel Health</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Acne Health</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Menopause Health</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Wellness Health</a></li>
              </ul>
            </div>
            <div>
              <h5 className="font-medium mb-4">Company</h5>
              <ul className="space-y-2 text-sm text-white/70">
                <li><Link to="/" className="hover:text-white transition-colors">Home</Link></li>
                <li><Link to="/team" className="hover:text-white transition-colors">Our Team</Link></li>
                <li><a href="https://havenintegrativehealth.janeapp.com/login" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors">Book Consultation</a></li>
                <li><a href="#" className="hover:text-white transition-colors">FAQs</a></li>
              </ul>
            </div>
            <div>
              <h5 className="font-medium mb-4">Legal</h5>
              <ul className="space-y-2 text-sm text-white/70">
                <li><a href="#" className="hover:text-white transition-colors">Privacy Policy</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Terms of Service</a></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-white/20 pt-8 text-center text-sm text-white/60">
            <p>&copy; 2024 Haven Integrative Health Consulting Inc. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  )
}

export default Team
